import axios from 'axios';
import { User, County, Ward, Project, Feedback, Analytics, Document, ForumPost, Media } from '../types';

// Dynamic API base URL - uses environment variable or defaults to localhost
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

console.log('🌐 API Base URL:', API_BASE);
console.log('🌐 Environment:', process.env.NODE_ENV);

const api = axios.create({
  baseURL: API_BASE,
  timeout: 45000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Retry function for failed requests
const retryRequest = async (fn: () => Promise<any>, retries = 2, delay = 2000): Promise<any> => {
  try {
    return await fn();
  } catch (error: any) {
    if (retries > 0 && (error.response?.status === 503 || error.code === 'ECONNABORTED')) {
      console.log(`Retrying request in ${delay}ms... (${retries} retries left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(fn, retries - 1, delay * 1.5);
    }
    throw error;
  }
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add CSRF protection
  config.headers['X-CSRF-Token'] = sessionStorage.getItem('csrf-token') || '';
  return config;
});

// Handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout - backend may be starting up');
      return Promise.reject(new Error('Request timeout. The server may be starting up, please try again in a moment.'));
    }
    
    if (error.response?.status === 503) {
      console.error('Service unavailable - backend may be starting up');
      return Promise.reject(new Error('Service temporarily unavailable. The server may be starting up, please try again in a moment.'));
    }
    
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    if (!error.response) {
      console.error('Network error or CORS issue');
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
};

// County API
export const countyAPI = {
  getAll: () => retryRequest(() => api.get<County[]>('/counties')),
  getById: (name: string) => retryRequest(() => api.get<County>(`/counties/${name}`)),
  getAnalytics: (name: string) => retryRequest(() => api.get<Analytics>(`/analytics/county/${name}`)),
};

// Ward API
export const wardAPI = {
  getByCounty: (countyName: string) => retryRequest(() => api.get<Ward[]>(`/counties/${countyName}/wards`)),
  getById: (wardId: string) => retryRequest(() => api.get<Ward>(`/wards/${wardId}`)),
  create: (wardData: Partial<Ward>) => api.post('/admin/wards', wardData),
};

// Project API
export const projectAPI = {
  getByWard: (wardId: string) => retryRequest(() => api.get<Project[]>(`/wards/${wardId}/projects`)),
  getById: (projectId: string) => retryRequest(() => api.get<Project>(`/admin/projects/${projectId}`)),
  getPublic: (county?: string, ward?: string, sector?: string) => 
    retryRequest(() => api.get<Project[]>('/projects', { params: { county, ward, sector } })),
  getPending: () => retryRequest(() => api.get<Project[]>('/admin/projects/pending')),
  create: (projectData: Partial<Project>) => api.post('/admin/projects', projectData),
  update: (projectId: string, data: Partial<Project>) => 
    api.put(`/admin/projects/${projectId}`, data),
  delete: (projectId: string) => api.delete(`/admin/projects/${projectId}`),
  updateProgress: (projectId: string, data: { progress: number, notes: string, updated_by: string }) => 
    api.put(`/admin/projects/${projectId}/progress`, data),
  approve: (projectId: string, data: { action: string, reason?: string, notes?: string }) => 
    api.put(`/admin/projects/${projectId}/approve`, data),
};

// Feedback API
export const feedbackAPI = {
  submit: (feedbackData: FormData | Partial<Feedback>, customHeaders?: any) => {
    let config: any = {};
    
    if (feedbackData instanceof FormData) {
      // Don't set Content-Type for FormData, let browser set it with boundary
      config = {};
    } else {
      config = { headers: { 'Content-Type': 'application/json', ...customHeaders } };
    }
    
    return api.post('/feedback', feedbackData, config);
  },
  getPublic: (county?: string, ward?: string) => 
    api.get<Feedback[]>('/feedback/public', { params: { county, ward } }),
  getAdminFeedback: () => 
    api.get<Feedback[]>('/admin/feedback'),
  updateFeedback: (feedbackId: string, data: { status?: string, is_public?: boolean }) => 
    api.put(`/admin/feedback/${feedbackId}`, data),
  deleteFeedback: (feedbackId: string) => 
    api.delete(`/admin/feedback/${feedbackId}`),
  // Legacy methods for backward compatibility
  getAdmin: (status?: string, county?: string) => 
    api.get<Feedback[]>('/admin/feedback', { params: { status, county } }),
  moderate: (feedbackId: string, data: { status: string, is_public: boolean }) => 
    api.put(`/admin/feedback/${feedbackId}`, data),
  respond: (feedbackId: string, data: { response: string, responder_role: string }) => 
    api.put(`/admin/feedback/${feedbackId}/respond`, data),
  update: (feedbackId: string, data: Partial<Feedback>) => 
    api.put(`/admin/feedback/${feedbackId}`, data),
  delete: (feedbackId: string) => 
    api.delete(`/admin/feedback/${feedbackId}`)
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get<User[]>('/admin/users'),
  createUser: (userData: Partial<User>) => api.post('/admin/users', userData),
  updateUser: (userId: string, data: Partial<User>) => api.put(`/admin/users/${userId}`, data),
  getPendingProjects: () => api.get<Project[]>('/admin/projects/pending'),
  getWardProjects: (wardId: string) => api.get<Project[]>(`/admin/wards/${wardId}/projects`),
  approveProject: (projectId: string, data: { action: string, reason?: string, notes?: string }) => 
    api.put(`/admin/projects/${projectId}/approve`, data),
  uploadBudget: (budgetData: any) => api.post('/admin/budgets', budgetData),
  getBudgets: () => api.get('/budgets'),
  approveBudget: (budgetId: string, data: { action: string, reason?: string }) => 
    api.put(`/admin/budgets/${budgetId}/approve`, data),
  uploadEvidence: (projectId: string, files: FormData) => 
    api.post(`/admin/projects/${projectId}/evidence`, files, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  flagIssue: (issueData: any) => api.post('/admin/issues', issueData)
};

// User API (keeping for backward compatibility)
export const userAPI = {
  getAll: () => api.get<User[]>('/admin/users'),
  getById: (userId: string) => api.get<User>(`/admin/users/${userId}`),
  create: (userData: Partial<User>) => api.post('/admin/users', userData),
  update: (userId: string, data: Partial<User>) => api.put(`/admin/users/${userId}`, data),
  delete: (userId: string) => api.delete(`/admin/users/${userId}`),
};

// Document API
export const documentAPI = {
  getAll: () => retryRequest(() => api.get<Document[]>('/documents')),
  getById: (id: string) => retryRequest(() => api.get<Document>(`/documents/${id}`)),
  upload: (formData: FormData) => api.post('/admin/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id: string) => api.delete(`/admin/documents/${id}`),
};

// Budget API
export const budgetAPI = {
  getAll: () => retryRequest(() => api.get('/budgets')),
  upload: (budgetData: any) => api.post('/admin/budgets', budgetData),
  approve: (budgetId: string, data: { action: string, reason?: string }) => 
    api.put(`/admin/budgets/${budgetId}/approve`, data),
};

// Media API
export const mediaAPI = {
  getAll: (type?: string, county?: string, ward?: string, project_id?: string) => 
    retryRequest(() => api.get('/media', { params: { type, county, ward, project_id } })),
  upload: (formData: FormData) => api.post('/admin/media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (mediaId: string, data: { title?: string, description?: string, is_public?: boolean }) => 
    api.put(`/admin/media/${mediaId}`, data),
  delete: (mediaId: string) => api.delete(`/admin/media/${mediaId}`),
};

// Forum API
export const forumAPI = {
  getPosts: () => api.get<ForumPost[]>('/forum/posts'),
  createPost: (postData: Partial<ForumPost>) => api.post('/forum/posts', postData),
  rateProject: (projectId: string, rating: number, comment?: string) => 
    api.post(`/projects/${projectId}/rate`, { rating, comment }),
};

// Issues API
export const issuesAPI = {
  getAll: (status?: string, ward_id?: string) =>
    api.get('/admin/issues', { params: { status, ward_id } }),
  create: (data: any) => api.post('/admin/issues', data),
  update: (issueId: string, data: any) => api.put(`/admin/issues/${issueId}`, data),
  delete: (issueId: string) => api.delete(`/admin/issues/${issueId}`),
};

// Forum comments & likes
export const forumCommentsAPI = {
  like: (postId: string) => api.post(`/forum/posts/${postId}/like`),
  addComment: (postId: string, data: { content: string; author: string }) =>
    api.post(`/forum/posts/${postId}/comments`, data),
};

// Bulk project approval
export const bulkApproveAPI = {
  approve: (projectIds: string[], action: 'approve' | 'reject') =>
    api.put('/admin/projects/bulk-approve', { project_ids: projectIds, action }),
};

// Health check
export const healthAPI = {
  check: () => retryRequest(() => api.get('/health')),
};

// Volunteer API
export const volunteerAPI = {
  apply: (applicationData: any) => api.post('/volunteer/apply', applicationData),
  getApplications: (wardId?: string, status?: string) =>
    api.get('/admin/volunteers/applications', { params: { ward_id: wardId, status } }),
  reviewApplication: (applicationId: string, data: { action: string, notes?: string, reviewed_by: string }) =>
    api.put(`/admin/volunteers/applications/${applicationId}/review`, data),
  createEngagement: (formData: FormData) => api.post('/volunteer/engagements', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyEngagements: (conductedBy: string) =>
    api.get('/engagements/my', { params: { conducted_by: conductedBy } }),
  updateEngagement: (id: string, data: any) => api.put(`/engagements/${id}`, data),
  deleteEngagement: (id: string) => api.delete(`/engagements/${id}`),
  getMyDocuments: (uploadedBy: string) =>
    api.get('/documents/my', { params: { uploaded_by: uploadedBy } }),
  deleteDocument: (id: string) => api.delete(`/documents/${id}`),
};

export default api;